import { matricesApi } from '@/lib/api';
import { useEffectiveRole } from '@/lib/effective-role';
import {
  WIZARD_STEPS,
  WIZARD_STEP_LABELS,
  type WizardStep,
  deriveInitialStep,
} from '@/lib/wizard-step';
import type {
  CompanyWithSetup,
  Dimension,
  DimensionInput,
  MatrixPivot,
  MatrixWithDimensions,
  PatchMatrixInput,
  User,
} from '@mss_creator/shared';
import { Stepper } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { DimensionsStep } from './dimensions-step';
import { RecapitulatifStep } from './recapitulatif-step';
import { SetupReviewStep } from './setup-review-step';
import { SujetContexteStep } from './sujet-contexte-step';

interface MatrixWizardProps {
  matrix: MatrixWithDimensions;
  company: CompanyWithSetup;
  me: User;
  /** The wizard route's `derivedFrom` search param (issue #16). */
  derivedFrom?: string | undefined;
  /** The wizard route's `pivotReset` search param (issue #16). */
  pivotReset?: boolean | undefined;
}

/**
 * Wizard step navigation lives entirely in this component's in-memory state
 * (design doc: "état de formulaire unique, pas une suite d'écrans
 * indépendants") — the four steps below are not separate router pages.
 *
 * The outer div is the wizard's fixed-size frame (2026-08-18 rework): width
 * capped at `min(90vw, 1200px)`, height filling the app shell's `main`. Only
 * the steps container scrolls — the Stepper above it, and each step's own
 * sticky-bottom footer, stay pinned in view regardless of content length.
 */
export function MatrixWizard({ matrix, company, me, derivedFrom, pivotReset }: MatrixWizardProps) {
  const queryClient = useQueryClient();

  const role = useEffectiveRole(me);
  // An archived company is closed (issue #22): the wizard stays readable and
  // navigable, but nothing on it writes: the backend would answer 409
  // `COMPANY_ARCHIVED` to every one of those writes.
  const readOnly = Boolean(company.archivedAt);

  // A draft created from a matrix already has a sujet, a contexte and
  // dimensions, so the resume rule would land it on step 4. It opens on step
  // 1 instead, where its banner is and its pivot is chosen (issue #16,
  // « Création depuis une matrice »).
  const [currentStep, setCurrentStep] = useState<WizardStep>(() =>
    derivedFrom !== undefined && matrix.state === 'brouillon' ? 1 : deriveInitialStep(matrix),
  );

  const [sujet, setSujet] = useState(matrix.sujet);
  const [contexte, setContexte] = useState(matrix.contexte);
  const [pivot, setPivot] = useState<MatrixPivot>(matrix.pivot);
  // Lifted so a step 3 -> step 2/4 -> step 3 round trip resumes from the
  // latest persisted list (with real ids/order) rather than resetting to the
  // matrix's dimensions as they were at the wizard's initial GET — each step
  // component unmounts when the wizard switches steps, so its own internal
  // form state doesn't survive the trip on its own.
  const [dimensions, setDimensions] = useState<Dimension[]>(matrix.dimensions);

  const isGenerating = matrix.state === 'génération_en_cours';
  const lastGenerationFailed =
    matrix.state === 'brouillon' && matrix.lastGenerationFailedAt !== null;
  const patchMutation = useMutation({
    mutationFn: (patch: PatchMatrixInput) => matricesApi.patch(matrix.id, patch),
  });
  const dimensionsMutation = useMutation({
    mutationFn: (input: DimensionInput[]) => matricesApi.replaceDimensions(matrix.id, input),
    onSuccess: (updated) => setDimensions(updated),
  });

  function handleSujetContexteSave(patch: PatchMatrixInput) {
    if (readOnly) return;
    if (patch.sujet !== undefined) setSujet(patch.sujet ?? null);
    if (patch.contexte !== undefined) setContexte(patch.contexte ?? null);
    patchMutation.mutate(patch);
  }

  function handleDimensionsSave(input: DimensionInput[]) {
    if (readOnly) return;
    dimensionsMutation.mutate(input);
  }

  function handleGenerationStarted() {
    // The generation-job route flips matrices.state to 'génération_en_cours'
    // synchronously before returning — invalidate right away rather than
    // waiting for wizard.$matrixId.tsx's own 3s poll to notice.
    void queryClient.invalidateQueries({ queryKey: ['matrix', matrix.id] });
  }

  return (
    <div className="flex h-full min-h-0 w-[min(90vw,1200px)] flex-col gap-6 py-6">
      <div className="shrink-0">
        {/*
         * The package Stepper only shows where the user is: steps are reached
         * with each step's own Retour / Suivant buttons.
         */}
        <nav aria-label="Étapes de création de la matrice">
          <Stepper
            steps={WIZARD_STEPS.map((step) => WIZARD_STEP_LABELS[step])}
            current={currentStep - 1}
          />
        </nav>
      </div>

      {/*
       * All four steps stay mounted (visibility toggled via `hidden`)
       * instead of conditionally rendering the current one. RecapitulatifStep
       * relies on this: its manual-edit/conflict-dialog state must survive a
       * trip to steps 2/3 and back, which an unmount would silently wipe.
       * This div is the wizard's only scroll container — the Stepper above
       * and each step's own sticky footer stay pinned regardless of content.
       */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div hidden={currentStep !== 1}>
          <SetupReviewStep
            matrix={matrix}
            company={company}
            pivot={pivot}
            onPivotChange={setPivot}
            derivedFrom={derivedFrom}
            pivotReset={pivotReset}
            readOnly={readOnly}
            onContinue={() => setCurrentStep(2)}
          />
        </div>
        <div hidden={currentStep !== 2}>
          <SujetContexteStep
            matrixId={matrix.id}
            documentsLocked={isGenerating || readOnly}
            readOnly={readOnly}
            initialSujet={sujet}
            initialContexte={contexte}
            onSave={handleSujetContexteSave}
            onBack={() => setCurrentStep(1)}
            onContinue={() => setCurrentStep(3)}
          />
        </div>
        <div hidden={currentStep !== 3}>
          <DimensionsStep
            initialDimensions={dimensions}
            readOnly={readOnly}
            onSave={handleDimensionsSave}
            onBack={() => setCurrentStep(2)}
            onContinue={() => setCurrentStep(4)}
          />
        </div>
        <div hidden={currentStep !== 4}>
          <RecapitulatifStep
            matrixId={matrix.id}
            companyId={company.id}
            pivot={pivot}
            destinataire={matrix.destinataire}
            destinataireAutre={matrix.destinataireAutre}
            optionsFrozen={matrix.paliersFrozenAt !== null}
            sujet={sujet}
            contexte={contexte}
            dimensions={dimensions}
            paliers={matrix.paliers}
            matrixState={matrix.state}
            lastGenerationFailed={lastGenerationFailed}
            role={role}
            active={currentStep === 4}
            readOnly={readOnly}
            onBack={() => setCurrentStep(3)}
            onGenerationStarted={handleGenerationStarted}
          />
        </div>
      </div>
    </div>
  );
}
